import { useMemo } from 'react'
import { type ModuleAction, type ModuleManifest } from './types'
import { useAuth } from '@/lib/cerberus'

// Eagerly import all module manifests at build time.
// Each module exports a named `manifest` const of type ModuleManifest.
const rawModules = import.meta.glob('@/modules/*/manifest.ts', {
  eager: true,
}) as Record<string, { manifest: ModuleManifest }>

/**
 * Returns every manifest currently registered under `src/modules/*`.
 *
 * Exported so non-React code paths (e.g. `DomainProvider` resolving
 * `brand` / `primary_action_id` from the manifest matching the active
 * domain) can read the same registry that `useModules` consumes,
 * without going through a hook. Permission filtering is **not** applied
 * here — that is the caller's responsibility (see `useModules`).
 */
export function getRegisteredModules(): ModuleManifest[] {
  return Object.values(rawModules)
    .map((m) => m.manifest)
    .filter(Boolean)
}

/**
 * Permission format inherited from binding #06: `<system>:<scope>` (e.g.
 * `finance:admin`). Cerberus stores per-org grants as `{ domain, org_id }`
 * plus a top-level `is_super_admin` flag. A permission is granted if the
 * user is super-admin or has a grant whose `domain` matches `<system>`.
 * Scope (`:admin`, `:viewer`) is currently advisory only — Cerberus does
 * not model role tiers per domain yet, so any grant on the domain counts.
 */
function permissionAllowed(
  perm: string,
  hasGrant: (domain: string, orgId?: string) => boolean,
  isSuperAdmin: boolean,
): boolean {
  if (isSuperAdmin) return true
  if (perm === '*:admin') return false // not modeled — only super_admin satisfies
  const colonIdx = perm.indexOf(':')
  const domain = colonIdx === -1 ? perm : perm.slice(0, colonIdx)
  return hasGrant(domain)
}

/**
 * Returns the registered `ModuleAction` matching `actionId` across every
 * module manifest currently in the registry, or `undefined` when no
 * module declares it.
 *
 * Used by F3.4 (FAB) to resolve `useDomain().primary_action_id` against
 * the manifest so the shell does not need to import per-domain action
 * components. F3.2 ships only the lookup; consumers land in later PRs.
 *
 * The lookup walks every registered manifest — not just modules the
 * current user has access to — so that an action declared by a module
 * the user cannot see returns `undefined` only when the action_id is
 * unknown, not because permissions filtered it out. Permission checks
 * are the FAB's responsibility (or `useModules()`'s) once wired.
 */
export function useModuleAction(
  actionId: string | undefined | null,
): ModuleAction | undefined {
  return useMemo(() => {
    if (!actionId) return undefined
    for (const mod of getRegisteredModules()) {
      const match = mod.actions?.find((a) => a.action_id === actionId)
      if (match) return match
    }
    return undefined
  }, [actionId])
}

/**
 * Returns all module manifests accessible by the current user.
 * Routes are filtered: only routes where the user has at least one
 * of the required permissions are included.
 */
export function useModules(): ModuleManifest[] {
  const { status, hasGrant, isSuperAdmin } = useAuth()

  return useMemo(() => {
    if (status !== 'authed') return []
    const all = getRegisteredModules()
    return all
      .map((mod) => ({
        ...mod,
        routes: mod.routes.filter(
          (route) =>
            route.permissions.length === 0 ||
            route.permissions.some((perm) =>
              permissionAllowed(perm, hasGrant, isSuperAdmin),
            ),
        ),
      }))
      .filter((mod) => mod.routes.length > 0)
  }, [status, hasGrant, isSuperAdmin])
}
